import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import AuthForm from './AuthForm';
import SafeIcon from '../../common/SafeIcon';

export default function AdminGate({ children }) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'unauthenticated' | 'admin' | 'denied'

  useEffect(() => {
    let isMounted = true;

    async function checkAccess(session) {
      if (!session) {
        if (isMounted) setStatus('unauthenticated');
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', session.user.id)
          .single();


        if (!isMounted) return;

        if (error) {
          console.error('Profile fetch error:', error);
          setStatus('denied');
          return;
        }

        setStatus(data?.is_admin === true ? 'admin' : 'denied');
      } catch (err) {
        console.error('Admin check failed:', err);
        if (isMounted) setStatus('denied');
      }
    }

    // Initial session check
    supabase.auth.getSession().then(({ data: { session } }) => {
      checkAccess(session);
    });

    // Listen for auth changes (login / logout)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (isMounted) {
        // Always reset to loading while we verify the new session
        setStatus('loading');
        checkAccess(session);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleSignOut = async () => {
    setStatus('loading');
    await supabase.auth.signOut();
  };

  if (status === 'loading') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blush-300"></div>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4 bg-warmwhite">
        <AuthForm initialMode="login" allowSignup={false} />
      </div>
    );
  }

  if (status === 'denied') {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4 bg-warmwhite text-center">
        <div className="glass-panel p-10 max-w-md space-y-6">
          <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto">
            <SafeIcon name="AlertCircle" className="text-4xl text-red-400" />
          </div>
          <h2 className="text-3xl font-serif text-charcoal">Access Denied</h2>
          <p className="text-sage-500 text-lg">
            You do not have administrator privileges to access this area.
          </p>
          <button onClick={handleSignOut} className="btn-secondary w-full">
            Sign Out & Return Home
          </button>
        </div>
      </div>
    );
  }

  // status === 'admin'
  return children;
}
